import { ENV } from '@env';
import { Geometry } from 'geojson';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter } from 'rxjs/operators';

import {
  Input,
  Output,
  Component,
  OnDestroy,
  OnChanges,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Router } from '@angular/router';

import {
  SnCamera,
  SnLocation,
  SnPullProtocol,
  SnCameraUnmanagedType,
  SnCameraManagedType,
  SnCameraManagementType,
  SnCameraStatus,
  SnCameraAiDetectionType,
  SnExternalAppliance,
  SnCameraControlType,
  SnAppliance,
  FsAuthDigest,
} from '@lib/api/entities';
import { SnExternalAppliancesAPI } from '@lib/api/services';
import { SnApplianceFieldsGeneratorService } from '@lib/appliances';
import { SnCurrentOrganization, SnCurrentUser } from '@lib/auth';
import {
  ISnFormStep,
  ISnHasStepperForm,
  SnFormFieldConfig,
  SnFormUrlValidatorType,
  SnValidators,
} from '@lib/form';
import { SnFloorFieldsGeneratorService, SnLocationFieldsGeneratorService } from '@lib/locations';
import { t } from '@lib/translation';
import { ISnHttpSelectFormFieldConfig } from 'libs/form/http-select/src/lib/interfaces/http-select-form-field-config.interface';
import { SnDrawerRef } from 'libs/ui/drawer/src/lib/references';

import { SnCameraFieldsGeneratorService } from '../../services/camera-fields-generator.service';
import { AuthDigestWarningComponent } from '../auth-digest-warning/auth-digest-warning.component';
import { CameraAiGridDialogComponent } from '../camera-ai-grid-dialog';
import { CameraFormDrawerComponent } from '../camera-form-drawer/camera-form-drawer.component';
import { CameraRulesDialogComponent } from '../camera-rules-dialog/camera-rules-dialog.component';
import { SnCameraScheduleDialogComponent } from '../camera-schedule-dialog';

@Component({
  selector: 'sn-camera-form',
  templateUrl: './camera-form.component.html',
  styleUrls: ['./camera-form.component.scss'],
})
export class SnCameraFormComponent implements ISnHasStepperForm, OnDestroy, OnChanges, OnInit {
  @Input()
  public readonly form: UntypedFormArray;

  @Input()
  public isLoading: boolean;

  @Input()
  public geometry: Geometry;

  @Input()
  get camera(): SnCamera {
    return this._camera;
  }
  set camera(value: SnCamera) {
    this._camera = value ? value.clone() : new SnCamera();
    this.isNew = !!this._camera && !this._camera.id;
    this._patchForm(this._camera);
    if (this._camera?.status === 'Up' && this._camera?.playbackUrls) {
      const currentTime = new Date().getTime();
      const timeShift = Math.floor(currentTime / 1000) - 30;
      const thumbnail = document.querySelector('.thumbnailSnapshot');
      if (this._camera?.playbackUrls?.main.local?.mp4thumbnail) {
        const mp4Url = this._camera?.playbackUrls?.main.local?.getMp4Thumbnails(timeShift);
        if (thumbnail) {
          thumbnail.setAttribute('src', mp4Url);
        }
      } else if (this._camera?.playbackUrls?.main.cloud?.mp4thumbnail) {
        if (thumbnail) {
          const mp4Url = this._camera?.playbackUrls?.main.cloud?.getMp4Thumbnails(timeShift);
          thumbnail.setAttribute('src', mp4Url);
        }
      }
    }
    this.formSteps = this._getFormSteps();
  }

  @Output()
  public readonly send = new EventEmitter<Object>();

  @Output()
  public readonly cancel = new EventEmitter<void>();

  public formModel: any = {};
  public formSteps: ISnFormStep[];
  public externalAppliance: SnExternalAppliance;

  public isNew: boolean;
  public isAi: boolean = true;
  public env = ENV.toLowerCase();
  public selectedAppliance: SnAppliance;

  private _camera: SnCamera;
  private _hasAiDetectionTypes: boolean;
  private _chosenType;

  private readonly _detectionTypes$ = new BehaviorSubject<any[]>([]);
  private readonly _externalCameras$ = new BehaviorSubject<any>([]);
  private readonly _destroy$ = new Subject<void>();
  private readonly _externalCameraApplianceList$ = new Subject<any[]>();

  private _cameraMake: string = null;
  private _cameraModel: string = null;

  constructor(
    public currentOrganization: SnCurrentOrganization,
    private readonly _cameraFieldsGeneratorService: SnCameraFieldsGeneratorService,
    private readonly _applianceFieldsGenerator: SnApplianceFieldsGeneratorService,
    private readonly _externalAppliancesAPI: SnExternalAppliancesAPI,
    private readonly _floorFieldsGenerator: SnFloorFieldsGeneratorService,
    private readonly _locationFieldsGenerator: SnLocationFieldsGeneratorService,
    private readonly _router: Router,
    private readonly _matdialog: MatDialog,
    private readonly _cd: ChangeDetectorRef,
    private readonly _currentUser: SnCurrentUser,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _drawerRef: SnDrawerRef<CameraFormDrawerComponent>,
  ) {}

  get detectionTypes$(): Observable<any[]> {
    return this._detectionTypes$.asObservable();
  }
  get externalCameras$(): Observable<any> {
    return this._externalCameras$.asObservable();
  }

  get isManaged(): boolean {
    return this.formModel.managementType === SnCameraManagementType.Managed;
  }

  get isUnmanaged(): boolean {
    return this.formModel.managementType === SnCameraManagementType.Unmanaged;
  }

  get isExternal(): boolean {
    return this.formModel.managementType === SnCameraManagementType.External;
  }

  get isUp(): boolean {
    return this._camera.status === SnCameraStatus.Up;
  }

  get isRecording(): boolean {
    return this._camera.record;
  }

  get externalCameraApplianceList$(): Observable<any[]> {
    return this._externalCameraApplianceList$.asObservable();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.form) {
      this.formSteps.forEach(() => {
        const group = new UntypedFormGroup({});
        this.form.push(group);
      });
    }
  }

  public ngOnDestroy(): void {
    this.send.complete();
    this.cancel.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public ngOnInit(): void {
    this._loadExternalCamera();
  }

  public trackStep(index: number): any {
    return index;
  }

  public submitForm(): void {
    this.send.emit(this._getFormData());
  }

  public resetForm(): void {
    this.form.reset();
    this._patchForm(this._camera);
  }

  public cancelChanges(): void {
    this.cancel.emit();
  }

  public openAiGridDialog(): void {
    const dialog = this._matdialog.open(CameraAiGridDialogComponent, {
      data: this._camera,
      height: '90vh',
      width: '1065px', // Enough to display the video without the horizontal scrollbar on large screens
    });
    dialog
      .afterClosed()
      .pipe(
        filter((resp: any) => resp !== 'close'),
        takeUntil(this._destroy$),
      )
      .subscribe((points) => {
        this._camera.aiDetectionTypes[0].roi = points;
        this.formModel.aiDetectionTypes[0].roi = points;
      });
  }

  public openAiSchedulerDialog(): void {
    const dialog = this._matdialog.open(SnCameraScheduleDialogComponent, { data: this.formModel });
    dialog
      .afterClosed()
      .pipe(
        filter((resp) => !!resp && !!resp.schedules),
        takeUntil(this._destroy$),
      )
      .subscribe(({ timezone, schedules }) => {
        this.formModel.aiDetectionTypes[0].schedules = schedules;
        this.formModel.aiDetectionTypes[0].timezone = timezone;
      });
  }

  public openAiRulesDialog(): void {
    const dialog = this._matdialog.open(CameraRulesDialogComponent, {
      data: {
        camera: this.formModel,
        appliance: this.selectedAppliance,
      },
      height: '90vh',
      width: '1065px', // Enough to display the video without the horizontal scrollbar on large screens
    });

    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((camera) => {
        this._camera = camera;
        this._drawerRef.close();
      });
  }

  private _isLocationPage(): boolean {
    return this._router.url.indexOf('locations') > -1;
  }

  private _getFormData(): Object {
    const data = this.formModel;

    if (!this.isNew) {
      data.id = this._camera.id;
    }

    if (!data.aiDetectionTypes || !data.aiDetectionTypes[0]?.id || !data.isAiCamera) {
      data.aiDetectionTypes = [];
    }

    if (this.isAi && data.isAiEnabled) {
      data.aiAppliance = data.appliance;
    }

    if (this.isExternal) {
      data.unmanagedType = null;
    }
    data.idleTime = !this.isRecording ? 60 : null;
    data.group = data.groups?.map((group) => ({ id: group }));
    delete data.groups;
    if (!data.deepIntegrationSelect) {
      delete data.deepIntegrationMake;
    }
    return data;
  }

  private _patchForm(camera: SnCamera): void {
    const cameraFormModel = camera.toJSON({ groups: ['form'] });
    this.formModel = {
      ...cameraFormModel,
      geometry: camera.geometry || this.geometry,
      heading: camera.heading,
      excludedAdded: !this.isNew,
    };
  }

  private _onLocationChange(field: SnFormFieldConfig, location: SnLocation): void {
    if (location) {
      const geometryControl = field.parent.form.get('geometry');
      const newGeometry = location.geometry;
      geometryControl.setValue(newGeometry);
    }
  }

  private _onPullProtocolChange(field: SnFormFieldConfig): void {
    const value = field.formControl.value;
    const sslControl = field.form.controls.ssl;

    if (value === SnPullProtocol.RTMP) {
      sslControl.setValue(true);
    } else {
      sslControl.setValue(false);
    }
  }

  private _updateExternalAppliances(value: string, excluded: boolean): void {
    this._externalAppliancesAPI
      .getExternalApplianceCameraList(value, { exclude_added: excluded })
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        this._externalCameras$.next(response);
      });
  }

  private _changeCamName(field: any, event: any): void {
    this._cd.detectChanges();
    const selectedOption = field.templateOptions.options.find((o) => o.value === event);
    this.formModel.name = selectedOption.camera.name;
    this.formModel.model = selectedOption.camera.model;
    this.formModel.make = selectedOption.camera.manufacturer;
  }

  private _buildDynamicFields(detectionType: SnCameraAiDetectionType): void {
    const extraParams: any[] = detectionType.extraParams;
    const newFields: any[] = this.formSteps[2].fields?.filter((field) => {
      return !field.key?.toString().includes('aiDetectionTypes.0.extraParams.');
    });

    extraParams.forEach((param) => {
      switch (param.type) {
        case 'range':
          newFields.push({
            key: `aiDetectionTypes.0.extraParams.${param.key}`,
            type: 'slider',
            defaultValue: parseInt(param.default, 10),
            templateOptions: {
              label: param.label,
              min: param.step,
              max: parseInt(param.max, 10),
              step: param.step,
              thumbLabel: true,
              value: parseInt(param.default, 10),
              hideFieldUnderline: true,
              required: param.required,
            },
          });
          break;
        case 'display':
          newFields.push({
            key: `aiDetectionTypes.0.extraParams.${param.key}`,
            templateOptions: {
              hideFieldUnderline: true,
            },
            template: `<div>${param.label}</div>`,
          });
          break;
      }
    });

    this.formSteps[2].fields = [...newFields];
  }

  private _getFormSteps(): ISnFormStep[] {
    return [
      {
        label: t('cameras.general'),
        fields: [
          this._cameraFieldsGeneratorService.getNameField({
            templateOptions: {
              required: true,
            },
          }),
          this._cameraFieldsGeneratorService.getIsPublicField(),
          this._cameraFieldsGeneratorService.getCameraTypeField({
            templateOptions: {
              required: true,
            },
          }),
          this._cameraFieldsGeneratorService.getMakeField({}),
          this._cameraFieldsGeneratorService.getModelField(),
          this._cameraFieldsGeneratorService.getGroupsField(
            {},
            {
              pageSize: 20,
            },
          ),
          this._cameraFieldsGeneratorService.getTypeField(),
          this._cameraFieldsGeneratorService.getPtzControlMovementTypeField({
            hideExpression: () =>
              this.formModel.type !== SnCameraControlType.PTZ || !this._currentUser.isSupportUser,
          }),
          this._cameraFieldsGeneratorService.getPtzEnableArrowsField({
            hideExpression: () => this.formModel.type !== SnCameraControlType.PTZ,
          }),
          this._cameraFieldsGeneratorService.getPtzControlInvertX({
            hideExpression: () => this.formModel.type !== SnCameraControlType.PTZ,
          }),
          this._cameraFieldsGeneratorService.getPtzControlInvertY({
            hideExpression: () => this.formModel.type !== SnCameraControlType.PTZ,
          }),
          this._cameraFieldsGeneratorService.getPtzZoomField({
            hideExpression: () => this.formModel.type !== SnCameraControlType.PTZ,
          }),
          this._cameraFieldsGeneratorService.getDispositionField(),
          {
            templateOptions: {
              label: t('common.location'),
            },
            wrappers: ['form-field-group'],
            fieldGroup: [
              this._locationFieldsGenerator.getLocationSelectField(
                {
                  templateOptions: {
                    change: this._onLocationChange.bind(this),
                    skipIdIn: true,
                  },
                },
                {
                  pageSize: 20,
                  isOwned: true,
                },
              ),
              this._floorFieldsGenerator.getFloorSelectField('location', {
                hooks: {
                  onInit: (field: ISnHttpSelectFormFieldConfig) => {
                    field.parent.formControl
                      .get('location')
                      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this._destroy$))
                      .subscribe(() => {
                        field.reset();
                      });
                  },
                },
              }),
            ],
          },
          {
            templateOptions: {
              label: t('cameras.parentCamera'),
            },
            wrappers: ['form-field-group'],
            fieldGroup: [this._cameraFieldsGeneratorService.getParentCameraField(this._camera?.id)],
          },
          {
            templateOptions: {
              label: t('cameras.position'),
            },
            wrappers: ['form-field-group'],
            fieldGroup: [this._cameraFieldsGeneratorService.getGeometryField()],
            expressionProperties: {
              'templateOptions.disabled': () => !this.isNew,
            },
            hideExpression: () => (!this.isNew && this._isLocationPage() ? true : false),
          },
          {
            templateOptions: {
              label: t('cameras.directionality'),
              hideFieldUnderline: true,
            },
            wrappers: ['form-field-group'],
            fieldGroup: [
              this._cameraFieldsGeneratorService.getRadialField({
                templateOptions: {
                  change: (field) => {
                    this.formModel.heading = field.formControl.value;
                  },
                },
              }),
            ],
          },
          {
            templateOptions: {
              label: t('cameras.cameraViewSnapshot'),
              hideFieldUnderline: true,
            },
            wrappers: ['form-field-group'],
            hideExpression: () => this._camera?.status !== 'Up',
            template: '<div><video src="" class="thumbnail thumbnailSnapshot"></video></div>',
          },
        ],
      },
      {
        label: t('cameras.streamTab'),
        fields: [
          this._cameraFieldsGeneratorService.getManagementTypeField(),
          {
            key: 'deepIntegrationSelect',
            type: 'toggle',
            templateOptions: {
              label: t('cameras.deepIntegration'),
              change: (field, event) => {
                const index = field.parent.fieldGroup.findIndex(
                  (fGroup) => fGroup.key === 'deepIntegrationMake',
                );
                event.checked
                  ? (field.parent.fieldGroup[index].hide = false)
                  : (field.parent.fieldGroup[index].hide = true);
                this.formModel.deepIntegrationMake = event.checked
                  ? this.form.get('deepIntegrationMake')?.value
                  : null;
              },
            },
            hooks: {
              onInit: (field) => {
                const deepIntegrationMakeField = field.parent.fieldGroup.find(
                  (fGroup) => fGroup.key === 'deepIntegrationMake',
                );
                if (deepIntegrationMakeField.formControl.value) {
                  field.formControl.setValue(true);
                  deepIntegrationMakeField.hide = false;
                } else {
                  field.formControl.setValue(false);
                  deepIntegrationMakeField.hide = true;
                }
              },
            },
            hideExpression: () => !this.isManaged,
          },
          {
            key: 'deepIntegrationMake',
            type: 'select',
            hide: true,
            templateOptions: {
              appearance: 'fill',
              label: t('cameras.deepIntegrationMake'),
              options: [{ label: 'Bosch', value: SnCameraManagedType.BOSCH }],
            },
            hooks: {
              onInit: (field) => {
                const deepIntegrationMakeField = field.parent.fieldGroup.find(
                  (fGroup) => fGroup.key === 'deepIntegrationMake',
                );
                const deepIntegrationSelectField = field.parent.fieldGroup.find(
                  (fGroup) => fGroup.key === 'deepIntegrationSelect',
                );
                if (this.camera.deepIntegrationMake) {
                  field.formControl.setValue(true);
                  deepIntegrationMakeField.hide = false;
                  deepIntegrationMakeField.formControl.setValue(SnCameraManagedType.BOSCH);
                  deepIntegrationSelectField.formControl.setValue(true);
                  deepIntegrationSelectField.hide = false;
                } else {
                  field.formControl.setValue(null);
                  deepIntegrationMakeField.hide = true;
                }
              },
            },
          },

          {
            fieldGroup: [
              this._applianceFieldsGenerator.getApplianceSelectField(
                {
                  templateOptions: {
                    changeOnLoad: true,
                    change: (field, appliance) => {
                      this.selectedAppliance = appliance;
                      this._hasAiDetectionTypes =
                        appliance?.supportedAiDetections?.length > 0 ? true : false;
                      this.formModel.aiAppliance = appliance?.isAiAppliance ? appliance?.id : null;
                      const recordControl = field.form.get('record');
                      if (recordControl) {
                        if (appliance && appliance?.baseType.recordingAllowed) {
                          recordControl.enable();
                        } else {
                          recordControl.patchValue(false);
                          recordControl.disable();
                        }
                      }
                      this.isAi = appliance?.isAiAppliance;
                      this._detectionTypes$.next([
                        {
                          id: null,
                          name: t('cameras.noDetections'),
                        },
                        ...(appliance?.supportedAiDetections || []),
                      ]);
                      this._cd.detectChanges();
                    },
                  },
                  expressionProperties: {
                    'templateOptions.disabled': () => {
                      return !this.isNew && !this._currentUser.isSupportUser;
                    },
                  },
                },
                {
                  params: {
                    isCloud: false,
                  },
                },
              ),
              this._cameraFieldsGeneratorService.getLiveApplicationSelectField('appliance', {
                hooks: {
                  onInit: (field: ISnHttpSelectFormFieldConfig) => {
                    const applianceField = field.parent.formControl.get('appliance');
                    if (applianceField) {
                      applianceField.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
                        field.reset();
                      });
                    }
                  },
                },
              }),
              {
                fieldGroupClassName: 'camera-form-field-row',
                fieldGroup: [
                  this._cameraFieldsGeneratorService.getHostField({
                    className: 'camera-form-field-col-7',
                  }),
                  this._cameraFieldsGeneratorService.getPortField({
                    className: 'camera-form-field-col-5',
                  }),
                ],
              },
              this._cameraFieldsGeneratorService.getUsernameField(),
              this._cameraFieldsGeneratorService.getPasswordField(),
              this._cameraFieldsGeneratorService.getPathField(),
              this._cameraFieldsGeneratorService.isSubPathField({
                hooks: {
                  onInit: (field: ISnHttpSelectFormFieldConfig) => {
                    const transcodeField = field.parent.formControl.get('transcode');
                    if (transcodeField) {
                      field.parent.formControl
                        .get('transcode')
                        .valueChanges.pipe(takeUntil(this._destroy$))
                        .subscribe((value) => {
                          field.formControl.setValue(!value);
                        });
                    }
                  },
                },
                expressionProperties: {
                  'templateOptions.disabled': (model) => {
                    if (this.formModel.transcode) {
                      this.formModel.isSubEnabled = false;
                    }
                    return this.formModel.transcode;
                  },
                },
              }),
              this._cameraFieldsGeneratorService.getSubPathField({
                hideExpression: () => {
                  if (!this.formModel.isSubEnabled || this.formModel.transcode) {
                    this.formModel.pathSub = null;
                  }
                  return !this.formModel.isSubEnabled || this.formModel.transcode;
                },
              }),
              this._cameraFieldsGeneratorService.getProtocolField({
                templateOptions: {
                  change: this._onPullProtocolChange.bind(this),
                },
              }),
              this._cameraFieldsGeneratorService.getAuthDigestField({
                hide: !this._currentOrganization.settings.envIsControlled,
                templateOptions: {
                  change: (field, event) => {
                    if (event.value === FsAuthDigest.MD5 && event?.source) {
                      this._matdialog
                        .open(AuthDigestWarningComponent, {
                          width: '500px',
                        })
                        .afterClosed()
                        .pipe(takeUntil(this._destroy$))
                        .subscribe((res) => {
                          if (!res) {
                            this.formModel.authDigest = FsAuthDigest.SHA256;
                            field.formControl.setValue(FsAuthDigest.SHA256);
                            this._cd.detectChanges();
                          }
                        });
                    }
                  },
                },
              }),
              this._cameraFieldsGeneratorService.getSslField({
                expressionProperties: {
                  'templateOptions.disabled': (model) => model.protocol !== SnPullProtocol.RTMP,
                },
              }),
              this._cameraFieldsGeneratorService.getRecordField(),
              this._cameraFieldsGeneratorService.getLoopDurationField({
                hideExpression: () => !this.formModel.record,
              }),
              this._cameraFieldsGeneratorService.getTranscodeField(),
              this._cameraFieldsGeneratorService.getTranscodeSourceProtocol({
                hideExpression: () => !this.formModel.transcode,
              }),
              this._cameraFieldsGeneratorService.getTranscodeCommand({
                hideExpression: () => !this.formModel.transcode,
              }),
              {
                key: 'isAiCamera',
                type: 'drag-slide-toggle',
                templateOptions: {
                  toggleLabel: t('cameras.enableDetections'),
                  change: (field, event) => {
                    this.formModel.aiAppliance = event.checked ? this.formModel.appliance : null;
                  },
                },
                hooks: {
                  onInit: (field) => {
                    this.formModel.aiAppliance = field.formControl.value
                      ? this.formModel.appliance
                      : null;

                    field.formControl.valueChanges
                      .pipe(takeUntil(this._destroy$))
                      .subscribe((value) => {
                        /* Hide expression hides form step but not from the form */
                        if (value && this._hasAiDetectionTypes && this.formSteps.length < 3) {
                          this.formSteps.push(this._getFormSteps()[2]);
                          this.form.controls.push(new UntypedFormGroup({}));
                        } else if (
                          !value &&
                          !this._hasAiDetectionTypes &&
                          this.formSteps.length >= 3
                        ) {
                          this.formSteps.pop();
                          this.form.controls.pop();
                          this.form.updateValueAndValidity();
                        }
                      });
                  },
                },
                hideExpression: () => !this.isAi,
              },
            ],
            hide: this.isUnmanaged,
            hideExpression: () => this.isUnmanaged || this.isExternal,
            expressionProperties: {
              'templateOptions.disabled': () => this.isUnmanaged,
            },
          },
          {
            fieldGroup: [
              this._cameraFieldsGeneratorService.getUnmanagedTypeField({
                defaultValue: SnCameraUnmanagedType.JPEG,
                templateOptions: {
                  change: (field) => {
                    const unmanagedUrlControl = field.form.get('unmanagedUrl');
                    unmanagedUrlControl.updateValueAndValidity();
                  },
                },
              }),
              this._cameraFieldsGeneratorService.getUnmanagedUrField({
                templateOptions: {
                  required: true,
                },
                validators: {
                  url: (control: AbstractControl) => {
                    if (this.formModel.hasOwnProperty('unmanagedType')) {
                      let urlType: SnFormUrlValidatorType = 'http';
                      switch (this.formModel.unmanagedType) {
                        case SnCameraUnmanagedType.HLS:
                          urlType = 'hls';
                          break;
                        case SnCameraUnmanagedType.SLDP:
                          urlType = 'ws';
                          break;
                      }

                      const validatorFn = SnValidators.url(urlType);
                      const validation = validatorFn(control);

                      return !validation;
                    }

                    return true;
                  },
                },
              }),
              {
                key: 'unmanagedParams',
                fieldGroup: [
                  this._cameraFieldsGeneratorService.getRefreshTimeField({
                    defaultValue: 5,
                    hideExpression: () => {
                      return this.formModel.unmanagedType !== SnCameraUnmanagedType.JPEG;
                    },
                  }),
                ],
              },
            ],
            hide: false,
            hideExpression: () => this.isManaged || this.isExternal,
            expressionProperties: {
              'templateOptions.disabled': () => this.isManaged,
            },
          },
          {
            fieldGroup: [
              this._cameraFieldsGeneratorService.getExternalApplianceSelectField({
                templateOptions: {
                  changeOnLoad: true,
                  change: (field, value) => {
                    this.externalAppliance = value;
                  },
                },
                hooks: {
                  afterViewInit: (field) => {
                    field.formControl.valueChanges
                      .pipe(takeUntil(this._destroy$))
                      .subscribe((value) => {
                        const excluded = this.isNew;
                        this._updateExternalAppliances(value, excluded);
                      });
                  },
                },
              }),
              this._cameraFieldsGeneratorService.getExternalCameraSelectField({
                templateOptions: {
                  emptyOption: true,
                  options: this.externalCameraApplianceList$,
                  change: this._changeCamName.bind(this),
                },
                hooks: {
                  onInit: (field) => {
                    this.externalCameras$.pipe(takeUntil(this._destroy$)).subscribe((res) => {
                      if (!res?.results) {
                        return;
                      }
                      const options = res?.results?.map((item: any) => {
                        return {
                          label: item?.name,
                          value: item?.id,
                          camera: item,
                        };
                      });
                      if (options) {
                        field.templateOptions.options = [...options];
                        this._cd.detectChanges();
                        if (field.templateOptions.updateOptions) {
                          field.templateOptions.updateOptions();
                        }
                      }
                    });
                  },
                },
                hideExpression: () => !this.formModel.externalAppliance,
              }),
              {
                key: 'exclude_added',
                type: 'checkbox',
                defaultValue: true,
                templateOptions: {
                  label: 'Show All Cameras',
                  change: (field, event) => {
                    this._externalAppliancesAPI
                      .getExternalApplianceCameraList(this.formModel.externalAppliance, {
                        exclude_added: !event.checked,
                      })
                      .pipe(takeUntil(this._destroy$))
                      .subscribe((response) => {
                        const externalCameraOptions = response.results.map((item) => {
                          return {
                            label: item?.name,
                            value: item?.id,
                            camera: item,
                          };
                        });
                        this._externalCameraApplianceList$.next(externalCameraOptions);
                        this._externalCameras$.next(response);
                      });
                  },
                },
                hideExpression: () => !this.formModel.externalAppliance,
              },
              {
                fieldGroup: [
                  this._applianceFieldsGenerator.getApplianceSelectField(
                    {
                      templateOptions: {
                        changeOnLoad: true,
                        change: (field, appliance) => {
                          this._hasAiDetectionTypes =
                            appliance?.supportedAiDetections?.length > 0 ? true : false;
                          this.formModel.aiAppliance = appliance?.isAiAppliance
                            ? appliance?.id
                            : null;
                          const recordControl = field.form.get('record');
                          if (recordControl) {
                            if (appliance && appliance?.baseType.recordingAllowed) {
                              recordControl.enable();
                            } else {
                              recordControl.patchValue(false);
                              recordControl.disable();
                            }
                          }
                          this.isAi = appliance?.isAiAppliance;
                          this._detectionTypes$.next([
                            {
                              id: null,
                              name: 'No Detections',
                            },
                            ...(appliance?.supportedAiDetections || []),
                          ]);
                          this._cd.detectChanges();
                        },
                      },
                    },
                    {
                      params: {
                        isCloud: false,
                      },
                    },
                  ),
                  this._cameraFieldsGeneratorService.getTranscodeField(),
                  this._cameraFieldsGeneratorService.getTranscodeSourceProtocol({
                    hideExpression: () => !this.formModel.transcode,
                  }),
                  this._cameraFieldsGeneratorService.getTranscodeCommand({
                    hideExpression: () => !this.formModel.transcode,
                  }),
                  this._cameraFieldsGeneratorService.getRecordField(),
                  this._cameraFieldsGeneratorService.getLoopDurationField({
                    hideExpression: () => !this.formModel.record,
                  }),
                ],
                hideExpression: () => this.externalAppliance?.type?.toLowerCase() !== 'verkada',
              },
            ],
            hideExpression: () => !this.isExternal,
          },
        ],
      },
      {
        label: t('cameras.detections'),
        fields: [
          {
            key: 'aiDetectionTypes.0.id',
            type: 'select',
            templateOptions: {
              label: t('cameras.detectionType'),
              valueProp: 'id',
              labelProp: 'name',
              change: (field, event) => {
                if (!event.value) {
                  return;
                }
                field.templateOptions.options = this.detectionTypes$;
                const types = this._detectionTypes$.getValue();
                this._chosenType = types.find((type) => type.id === event.value);
                if (this._chosenType) {
                  if (this._chosenType.allowedLabels.length > 0) {
                    this.formModel.aiDetectionTypes[0].allowedLabels =
                      this._chosenType.allowedLabels;
                    const labelField = field.parent.fieldGroup.find(
                      (group) => group.key === 'aiDetectionTypes.0.labels',
                    );
                    labelField.templateOptions.options = this._chosenType.allowedLabels.map(
                      (v) => ({ label: v, value: v }),
                    );
                  }
                  if (this._chosenType.detectionTimeout) {
                    const timeoutField = field.parent.fieldGroup.find(
                      (group) => group.key === 'aiDetectionTypes.0.detectionTimeout',
                    );
                    timeoutField.formControl.setValue(this._chosenType.detectionTimeout);
                  }
                  this._buildDynamicFields(this._chosenType);
                }

                if (!this.formModel.aiDetectionTypes[0].schedules) {
                  this.formModel.aiDetectionTypes[0].schedules = ['* * * * *'];
                }
              },
            },
            hooks: {
              onInit: (field) => {
                field.templateOptions.options = this.detectionTypes$;
                this.detectionTypes$.pipe(takeUntil(this._destroy$)).subscribe((types) => {
                  this._chosenType = types.find((type) => type.id === field.formControl.value);
                  if (this._chosenType && this._chosenType?.extraParams?.length) {
                    this._buildDynamicFields(this._chosenType);
                  }
                });
              },
            },
          },
          {
            type: 'input',
            key: 'aiDetectionTypes.0.detectionTimeout',
            defaultValue: 200,
            templateOptions: {
              type: 'number',
              label: t('cameras.detectionTimeout'),
              floatLabel: 'always',
            },
            hideExpression: () => !this.formModel.aiDetectionTypes[0]?.id,
          },
          {
            type: 'input',
            key: 'aiDetectionTypes.0.confidence',
            defaultValue: 50,
            templateOptions: {
              type: 'number',
              label: t('cameras.confidencePercentage'),
              floatLabel: 'always',
            },
            hideExpression: () => {
              return !this.formModel.aiDetectionTypes[0]?.id || !this._chosenType?.hasConfidence;
            },
          },
          {
            type: 'select',
            key: 'aiDetectionTypes.0.labels',
            hooks: {
              onInit: (field) => {
                const labels = this.formModel.aiDetectionTypes
                  ? this.formModel.aiDetectionTypes[0]?.allowedLabels || []
                  : [];
                field.templateOptions.options = labels.map((v) => ({ label: v, value: v }));
              },
            },
            templateOptions: {
              multiple: true,
              type: 'text',
              label: t('cameras.labels'),
              valueProp: 'value',
              labelProp: 'label',
              floatLabel: 'always',
            },
            hideExpression: () =>
              !this.formModel.aiDetectionTypes[0]?.id || !this._chosenType?.hasLabels,
          },
          {
            type: 'select',
            key: 'aiStreamType',
            defaultValue: 0,
            templateOptions: {
              label: t('cameras.aiStreamType'),
              floatLabel: 'always',
              options: [
                { label: t('cameras.default'), value: 0 },
                { label: t('cameras.sub'), value: 1 },
                { label: 'EXTERNAL', value: 2 },
              ],
            },
            expressionProperties: {
              'templateOptions.disabled': () => this.formModel.transcode,
            },
          },
          this._cameraFieldsGeneratorService.getAIPathExtField({
            hideExpression: () => this.formModel.aiStreamType !== 2,
          }),
        ],
        hideExpression: () => !this.formModel.isAiCamera && !this._hasAiDetectionTypes,
      },
    ];
  }

  private _loadExternalCamera(): void {
    this._externalAppliancesAPI
      .getExternalApplianceCameraList(this.formModel.externalAppliance, { exclude_added: false })
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        const externalCameraOptions = response.results.map((item) => {
          return {
            label: item?.name,
            value: item?.id,
            camera: item,
          };
        });
        this._externalCameras$.next(response);
        this._externalCameraApplianceList$.next(externalCameraOptions);
      });
  }

  protected onTabChange(event: MatTabChangeEvent): void {
    if (
      event.tab.textLabel === 'Stream' &&
      (this._cameraMake !== this.formModel.make || this._cameraModel !== this.formModel.model)
    ) {
      this._cameraMake = this.formModel.make;
      this._cameraModel = this.formModel.model;
      this._cameraFieldsGeneratorService.updateRtspPaths(this.formModel);
    }
  }
}
